import { useApi } from "../api/useApi.js";
import { ConfidencePill, ProvenancePopover } from "../components.js";
import "./evidence.css";

export type EvidenceCategory = "change" | "ci" | "deploy" | "other";
export interface EvidenceItem {
  eventId: number;
  category: EvidenceCategory;
  type: string;
  source: string;
  method: string;
  confidence: number;
  effectiveAt: string;
  content: string | null;
}
/** One estimate revision, oldest-first (NX-2630/E13.7 AC2 — `/work-items/:id/evidence`'s
 *  `estimateHistory` extension). Field names follow `EvidenceItem`'s own source/method/confidence/
 *  effectiveAt convention so the two lists read as one drill-to-evidence vocabulary. */
export interface EstimateHistoryItem {
  kind: string;
  value: number | string;
  unit: string | null;
  confidence: number;
  source: string;
  method: string;
  lineage: number[];
  effectiveAt: string; // valid time — when this estimate held
  recordedAt: string; // transaction time — when SeneSight learned it
}
export interface WorkItemEvidence {
  workItemId: string;
  confidence: number;
  evidence: EvidenceItem[];
  /** Optional — older/other callers of this shape (or a fixture predating NX-2634) simply have none,
   *  which renders as no estimate-history section rather than an error. */
  estimateHistory?: EstimateHistoryItem[];
}

const CATEGORY_LABEL: Record<string, string> = {
  change: "Changes",
  ci: "CI checks",
  deploy: "Deployments",
  other: "Other evidence",
};
const CATEGORY_ORDER: EvidenceCategory[] = ["change", "ci", "deploy", "other"];
const day = (iso: string): string => (iso.length >= 10 ? iso.slice(0, 10) : iso);

export function EvidenceContent({
  workItemId,
  loadingMessage = "Loading the evidence trail…",
}: {
  workItemId: string;
  loadingMessage?: string;
}): React.ReactNode {
  const state = useApi<WorkItemEvidence>(`/work-items/${workItemId}/evidence`);

  if (state.status === "loading") return <div className="lead">{loadingMessage}</div>;
  if (state.status === "error") {
    return state.error.status === 404 ? (
      <EmptyEvidence notFound />
    ) : (
      <div className="lead">Couldn&rsquo;t load evidence: {state.error.message}</div>
    );
  }

  return <EvidenceBody data={state.envelope.data} />;
}

/**
 * NX-2797 — the drill-in behind a Project HEALTH DIMENSION tile. Fetches the dimension's own
 * lineage trail (`/projects/:id/evidence/:dimension`) — the same event ids the health computation
 * carries — resolved server-side. Before this, the Project drawer fetched the WORK-ITEM endpoint
 * with the PROJECT id, which can never match, so every drill rendered a misleading
 * "connect a source" empty state.
 */
export function DimensionEvidenceContent({
  projectId,
  dimension,
  dimensionLabel,
}: {
  projectId: string;
  dimension: string;
  dimensionLabel: string;
}): React.ReactNode {
  const state = useApi<{ projectId: string; dimension: string; evidence: EvidenceItem[] }>(
    `/projects/${projectId}/evidence/${dimension}`,
  );
  if (state.status === "loading")
    return <div className="lead">Loading the evidence trail behind this dimension…</div>;
  if (state.status === "error") {
    return <div className="lead">Couldn&rsquo;t load evidence: {state.error.message}</div>;
  }
  const items = state.envelope.data.evidence;
  if (items.length === 0) {
    // Honest, dimension-specific absence — NEVER the "connect a source" prompt: reaching this
    // screen at all means a source is connected; this dimension simply has no observations yet.
    return (
      <div className="panel na empty enter" role="note">
        <div className="ei" aria-hidden="true">
          ⌀
        </div>
        <h3>No {dimensionLabel.toLowerCase()} evidence recorded yet</h3>
        <p>
          Nothing has been observed for this dimension so far. The trail fills in as ingested facts
          arrive — until then SeneSight shows the absence honestly rather than a fabricated zero.
        </p>
      </div>
    );
  }
  return (
    <EvidenceBody
      data={{ workItemId: projectId, confidence: 1, evidence: items, estimateHistory: [] }}
      subjectLabel={`the ${dimensionLabel.toLowerCase()} dimension`}
    />
  );
}

function EmptyEvidence({ notFound }: { notFound?: boolean }): React.ReactNode {
  return (
    <div className="panel na empty enter" role="note">
      <div className="ei" aria-hidden="true">
        ⌀
      </div>
      <h3>{notFound ? "No evidence trail for this work item" : "No connected evidence yet"}</h3>
      <p>
        This drill-to-proof trail is populated from your delivery sources. Connect a source (GitHub
        / Jira) to light up the commit, review, CI and deployment evidence behind every number —
        until then SeneSight shows N/A, not a fabricated zero.
      </p>
      <a className="btn" href="/connectors">
        Connect a source →
      </a>
    </div>
  );
}

export function EvidenceBody({
  data,
  subjectLabel = "this work item",
}: {
  data: WorkItemEvidence;
  subjectLabel?: string;
}): React.ReactNode {
  const items = data.evidence;
  const estimateHistory = data.estimateHistory ?? [];
  // NX-2634: a project/work item can carry estimate revisions with zero change/CI/deploy `items`
  // (e.g. drilled in from the Estimation panel) — that is real content, not an empty trail, so the
  // honest-empty state gates on BOTH lists rather than `items` alone.
  if (items.length === 0 && estimateHistory.length === 0) return <EmptyEvidence />;
  const inCategory = (category: EvidenceCategory): EvidenceItem[] =>
    items.filter((i) => i.category === category);
  const deploys = inCategory("deploy");

  return (
    <>
      <div className="lead">
        {items.length} piece{items.length === 1 ? "" : "s"} of evidence cite {subjectLabel} to
        source
        {estimateHistory.length > 0 &&
          ` + ${estimateHistory.length} estimate revision${estimateHistory.length === 1 ? "" : "s"}`}{" "}
        — each drillable to its provenance.
      </div>

      <div className="facts">
        <div className="fact">
          <div className="lab">Changes</div>
          <div className="fv">
            {inCategory("change").length}
            <small> commits + diffs</small>
          </div>
        </div>
        <div className="fact">
          <div className="lab">CI checks</div>
          <div className="fv">
            {inCategory("ci").length}
            <small> check runs</small>
          </div>
        </div>
        <div className="fact">
          <div className="lab">Deployments</div>
          <div className="fv">
            {deploys.length}
            <small> {deploys.length ? "in deploy log" : "none yet"}</small>
          </div>
        </div>
        <div className="fact">
          <div className="lab">Confidence</div>
          <div className="fv">
            <ConfidencePill value={data.confidence} />
          </div>
          <div className="fs">from coverage + freshness, not asserted</div>
        </div>
      </div>

      <div className="evsections">
        {CATEGORY_ORDER.filter((c) => inCategory(c).length > 0).map((category) => (
          <div key={category} className="panel pad enter">
            <div className="sec">{CATEGORY_LABEL[category]}</div>
            <div className="lin">
              {inCategory(category).map((item) => (
                <div key={item.eventId} className="lnode src">
                  <div className="lab">{item.type}</div>
                  <b>{item.source}</b>
                  <div className="fs">
                    {item.method} · {day(item.effectiveAt)}
                  </div>
                  {item.content !== null && <div className="content">{item.content}</div>}
                  <ProvenancePopover
                    source={item.source}
                    method={item.method}
                    confidence={item.confidence}
                    freshness={day(item.effectiveAt)}
                  >
                    cited to source →
                  </ProvenancePopover>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {estimateHistory.length > 0 && (
        <div className="panel pad enter">
          <div className="sec">Estimate history</div>
          <div className="lin">
            {estimateHistory.map((rev, index) => (
              // No stable id on a revision row (it is a bitemporal history entry, not an event) —
              // index is safe here: the list is a fixed snapshot from one fetch, never reordered or
              // spliced in place.
              <div key={index} className="lnode src">
                <div className="lab">{rev.kind}</div>
                <b>
                  {rev.value}
                  {rev.unit !== null ? ` ${rev.unit}` : ""}
                </b>
                <div className="fs">
                  {rev.method} · valid {day(rev.effectiveAt)} · recorded {day(rev.recordedAt)}
                </div>
                <ConfidencePill value={rev.confidence} />
                <ProvenancePopover
                  source={rev.source}
                  method={rev.method}
                  confidence={rev.confidence}
                  freshness={day(rev.effectiveAt)}
                >
                  cited to source →
                </ProvenancePopover>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="confnote evnote">
        Every figure on this trail is cited to a recorded source event — SeneSight asserts nothing
        it cannot show.
      </div>
    </>
  );
}
